import React, { useState, useEffect } from 'react';
import api from '../../api/client';
import '../kits/KitPopup.css';
import './WildmicrPopup.css';
import MarketplacesField, { ALL_MARKETPLACES } from './MarketplacesField';
import AlternateIdsField from './AlternateIdsField';
import ProductDetailsField from './ProductDetailsField';

const ReadyProductPopup = ({ isOpen, onClose, onSaved, editData = null }) => {
    const [form, setForm] = useState({
        SKU: '', productName: '', ASIN: '', FNSKU: '', GTIN: '', brandID: '',
        unitsPerBox: '', niche_category_id: '', marketplaces: ALL_MARKETPLACES,
    });
    const [brands, setBrands] = useState([]);
    const [niches, setNiches] = useState([]);
    const [saving, setSaving] = useState(false);
    const [error,  setError]  = useState('');

    useEffect(() => {
        if (!isOpen) return;
        api.get('/api/options/brands').then(r => setBrands(r.data.items || [])).catch(() => {});
        api.get('/api/options/niche_category').then(r => setNiches(r.data.items || [])).catch(() => {});
    }, [isOpen]);

    useEffect(() => {
        if (!isOpen) return;
        if (editData) {
            setForm({
                SKU:               editData.SKU               || '',
                productName:       editData.productName       || '',
                ASIN:              editData.ASIN              || '',
                FNSKU:             editData.FNSKU             || '',
                GTIN:              editData.GTIN              || '',
                brandID:           editData.brandID           || '',
                unitsPerBox:       editData.unitsPerBox       || '',
                niche_category_id: editData.niche_category_id || '',
                marketplaces:      editData.marketplaces != null ? editData.marketplaces : ALL_MARKETPLACES,
            });
        } else {
            setForm({ SKU: '', productName: '', ASIN: '', FNSKU: '', GTIN: '', brandID: '', unitsPerBox: '',
                      niche_category_id: '', marketplaces: ALL_MARKETPLACES });
        }
        setError('');
    }, [isOpen, editData]);

    if (!isOpen) return null;

    const setField = (f) => (e) => setForm(p => ({ ...p, [f]: e.target.value }));

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        if (!form.SKU.trim()) { setError('SKU is required'); return; }
        setSaving(true);
        try {
            if (editData) {
                await api.put(`/api/ready-product-popup/${editData.SKU}`, form);
            } else {
                await api.post('/api/ready-product-popup', form);
            }
            onSaved();
            onClose();
        } catch (err) {
            setError(err.response?.data?.error || 'Save failed');
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="kit-popup-overlay" onMouseDown={(e) => e.target === e.currentTarget && onClose()}>
            <div className="kit-popup wm-popup">

                <div className="kit-popup__header">
                    <div>
                        <div className="kit-popup__title">
                            {editData ? `Edit — ${editData.productName || editData.SKU}` : 'Add Ready Product'}
                        </div>
                        <div className="kit-popup__subtitle">
                            {editData ? 'Update ready product details' : 'Fill in ready product details'}
                        </div>
                    </div>
                    <button className="kit-popup__close" type="button" onClick={onClose}>
                        <svg width="14" height="14" viewBox="0 0 16 16" fill="none"><use xlinkHref="#closeBtn" /></svg>
                    </button>
                </div>

                <div className="wm-popup__body">
                    <div className="wm-fields-grid">

                        <div className="kit-field">
                            <span className="kit-field__label">SKU *</span>
                            <input className="kit-field__input" value={form.SKU}
                                readOnly={!!editData} onChange={setField('SKU')} placeholder="e.g. ORPRD_01" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Brand</span>
                            <select className="kit-field__input" value={form.brandID}
                                onChange={e => setForm(p => ({ ...p, brandID: e.target.value }))}>
                                <option value="">— Select brand —</option>
                                {brands.map(b => <option key={b.brandID} value={b.brandID}>{b.brandName}</option>)}
                            </select>
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Product Name</span>
                            <input className="kit-field__input" value={form.productName}
                                onChange={setField('productName')} placeholder="Product display name" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Niche</span>
                            <select className="kit-field__input" value={form.niche_category_id}
                                onChange={e => setForm(p => ({ ...p, niche_category_id: e.target.value }))}>
                                <option value="">— Select niche —</option>
                                {niches.map(n => <option key={n.niche_category_id} value={n.niche_category_id}>{n.niche_name}</option>)}
                            </select>
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">ASIN</span>
                            <input className="kit-field__input" value={form.ASIN}
                                onChange={setField('ASIN')} placeholder="Amazon ASIN" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">Units per Box</span>
                            <input className="kit-field__input" type="number" min="1"
                                value={form.unitsPerBox} onChange={setField('unitsPerBox')} placeholder="e.g. 1" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">FNSKU</span>
                            <input className="kit-field__input" value={form.FNSKU}
                                onChange={setField('FNSKU')} placeholder="Amazon FNSKU" />
                        </div>

                        <div className="kit-field">
                            <span className="kit-field__label">GTIN</span>
                            <input className="kit-field__input" value={form.GTIN}
                                onChange={setField('GTIN')} placeholder="Barcode / GTIN" />
                        </div>

                        <MarketplacesField
                            value={form.marketplaces}
                            onChange={v => setForm(p => ({ ...p, marketplaces: v }))}
                        />

                        {editData && <ProductDetailsField productType="readyproducts" sku={editData.SKU} />}
                        {editData && <AlternateIdsField productType="readyproducts" sku={editData.SKU} />}

                    </div>
                </div>

                <div className="kit-popup__footer">
                    <span className="kit-popup__error">{error && `⚠ ${error}`}</span>
                    <button className="kit-popup__submit" onClick={handleSubmit} disabled={saving}>
                        {saving ? 'Saving…' : (editData ? 'Save Changes' : 'Create Product')}
                    </button>
                </div>

            </div>
        </div>
    );
};

export default ReadyProductPopup;
